import { useState, useEffect } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Star, Pencil, ShieldCheck } from 'lucide-react';
import { useApprovalChains, ApprovalChain, ApprovalStepDef } from '../../hooks/useApprovalChains';
import { Card, Modal, Select, EmptyState, SkeletonCard } from '../../components/ui';
import Button from '../../components/Button';
import api from '../../services/api';

const APPROVER_TYPES = [
  { value: 'requester_line_manager', label: "Requester's line manager" },
  { value: 'requester_department_manager', label: "Requester's department manager" },
  { value: 'requester_manager_chain', label: 'Manager chain (N levels up)' },
  { value: 'role', label: 'Anyone with a role' },
  { value: 'specific_user', label: 'A specific user' },
];
const ROLES = ['line_manager', 'it_manager', 'top_management', 'admin'].map(r => ({ value: r, label: r.replace(/_/g, ' ') }));

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const blankStep = (order: number, topMgmt: boolean): ApprovalStepDef => topMgmt
  ? { stepOrder: order, name: `${ordinal(order)} Approver`, approverType: 'specific_user', approverRole: 'top_management', autoApproveIfRequesterIsApprover: true, allowReject: true }
  : { stepOrder: order, name: '', approverType: 'requester_line_manager', autoApproveIfRequesterIsApprover: true, allowReject: true };

interface TopMgmtUser { id: string; name: string; }

function ChainBuilder({
  chain, onClose, onSave,
}: {
  chain?: ApprovalChain;
  onClose: () => void;
  onSave: (c: Partial<ApprovalChain>) => Promise<boolean>;
}) {
  const isEdit = !!chain;
  const [name, setName] = useState(chain?.name ?? '');
  const [isDefault, setIsDefault] = useState(chain?.isDefault ?? false);
  const [isTopMgmtEscalation, setIsTopMgmtEscalation] = useState(chain?.isTopMgmtEscalation ?? false);
  const [steps, setSteps] = useState<ApprovalStepDef[]>(chain?.steps?.length ? chain.steps : [blankStep(1, false)]);
  const [saving, setSaving] = useState(false);
  const [topMgmtUsers, setTopMgmtUsers] = useState<TopMgmtUser[]>([]);

  useEffect(() => {
    api.get('/admin/users', { params: { role: 'top_management', limit: 200 } })
      .then(r => setTopMgmtUsers(r.data.users ?? []))
      .catch(() => {});
  }, []);

  const setStep = (i: number, patch: Partial<ApprovalStepDef>) =>
    setSteps(s => s.map((st, idx) => idx === i ? { ...st, ...patch } : st));
  const addStep = () => setSteps(s => [...s, blankStep(s.length + 1, isTopMgmtEscalation)]);
  const removeStep = (i: number) => setSteps(s => s.filter((_, idx) => idx !== i).map((st, idx) => ({ ...st, stepOrder: idx + 1 })));
  const moveStep = (i: number, dir: -1 | 1) => setSteps(s => {
    const j = i + dir;
    if (j < 0 || j >= s.length) return s;
    const copy = [...s];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy.map((st, idx) => ({ ...st, stepOrder: idx + 1 }));
  });

  const save = async () => {
    setSaving(true);
    const ok = await onSave({
      name, isDefault, isTopMgmtEscalation,
      steps: steps.map((s, i) => ({ ...s, stepOrder: i + 1, name: s.name || `Step ${i + 1}` })),
    });
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <Modal open onClose={onClose} size="lg" title={isEdit ? `Edit chain — ${chain!.name}` : 'New approval chain'}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={saving || !name}>{isEdit ? 'Save changes' : 'Create chain'}</Button>
      </>}>
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Chain name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. High-value purchases"
            className="w-full text-sm rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand" />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)} />
          Make this the default chain (used when no rule matches)
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={isTopMgmtEscalation}
            onChange={e => setIsTopMgmtEscalation(e.target.checked)} />
          Use this as the Top Management approval chain (Line Manager escalation)
        </label>
        {isTopMgmtEscalation && (
          <p className="text-xs text-gray-500 -mt-2">
            Add one step per named approver, in order — the Line Manager will see them as
            "1st — Name", "2nd — Name", etc. when transferring a ticket.
          </p>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-navy">Approval steps</span>
            <Button size="sm" variant="ghost" onClick={addStep}><Plus size={14} /> Add step</Button>
          </div>
          <div className="space-y-2">
            {steps.map((step, i) => (
              <div key={i} className="flex items-start gap-2 p-3 rounded-lg border border-gray-200 bg-gray-50">
                <div className="flex flex-col items-center mt-1">
                  <button onClick={() => moveStep(i, -1)} disabled={i === 0}
                    className="text-gray-300 hover:text-brand disabled:opacity-30 disabled:hover:text-gray-300">
                    <ChevronUp size={14} />
                  </button>
                  <span className="text-xs font-bold text-gray-400">{i + 1}</span>
                  <button onClick={() => moveStep(i, 1)} disabled={i === steps.length - 1}
                    className="text-gray-300 hover:text-brand disabled:opacity-30 disabled:hover:text-gray-300">
                    <ChevronDown size={14} />
                  </button>
                </div>
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input value={step.name} onChange={e => setStep(i, { name: e.target.value })} placeholder={`Step ${i + 1} name`}
                    className="text-sm rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30" />
                  <Select options={APPROVER_TYPES} value={step.approverType}
                    onChange={e => setStep(i, { approverType: e.target.value as ApprovalStepDef['approverType'] })} />
                  {step.approverType === 'role' && (
                    <Select options={ROLES} placeholder="Select role" value={step.approverRole ?? ''}
                      onChange={e => setStep(i, { approverRole: e.target.value })} />
                  )}
                  {step.approverType === 'specific_user' && (
                    <Select options={topMgmtUsers.map(u => ({ value: u.id, label: u.name }))}
                      placeholder="Select approver" value={step.approverUserId ?? ''}
                      onChange={e => setStep(i, {
                        approverUserId: e.target.value,
                        // Tagging approverRole here is what makes the engine treat this step
                        // as a real Top Management gate instead of falling back to IT Manager.
                        approverRole: isTopMgmtEscalation ? 'top_management' : step.approverRole,
                      })} />
                  )}
                  <label className="flex items-center gap-2 text-xs text-gray-500">
                    <input type="checkbox" checked={step.allowReject ?? false} onChange={e => setStep(i, { allowReject: e.target.checked })} />
                    Can reject
                  </label>
                </div>
                {steps.length > 1 && (
                  <button onClick={() => removeStep(i)} className="text-gray-300 hover:text-red-500 mt-2"><Trash2 size={15} /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default function ApprovalChains() {
  const { chains, loading, createChain, updateChain, deleteChain } = useApprovalChains();
  const [building, setBuilding] = useState(false);
  const [editing, setEditing]   = useState<ApprovalChain | null>(null);

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-navy">Approval Chains</h1>
          <p className="text-sm text-gray-500">Define the approval steps a ticket must pass through.</p>
        </div>
        <Button onClick={() => setBuilding(true)}><Plus size={15} /> New chain</Button>
      </div>

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2">{[0, 1].map(i => <SkeletonCard key={i} />)}</div>
      ) : chains.length === 0 ? (
        <Card><EmptyState title="No approval chains" description="Create one to start routing approvals." /></Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {chains.map(chain => (
            <Card key={chain.id} title={
              <span className="flex items-center gap-2 flex-wrap">
                {chain.name}
                {chain.isDefault && <span className="inline-flex items-center gap-1 text-xs text-amber-600"><Star size={12} /> default</span>}
                {chain.isTopMgmtEscalation && <span className="inline-flex items-center gap-1 text-xs text-violet-600"><ShieldCheck size={12} /> top mgmt escalation</span>}
                {!chain.isActive && <span className="text-xs text-gray-400">(inactive)</span>}
              </span>
            } actions={
              <span className="flex items-center gap-2">
                <button onClick={() => setEditing(chain)} className="text-gray-300 hover:text-brand"><Pencil size={15} /></button>
                <button onClick={() => deleteChain(chain.id)} className="text-gray-300 hover:text-red-500"><Trash2 size={15} /></button>
              </span>
            }>
              <ol className="space-y-1.5">
                {chain.steps.map(s => (
                  <li key={s.id ?? s.stepOrder} className="flex items-center gap-2 text-sm">
                    <span className="w-5 h-5 rounded-full bg-brand/10 text-brand text-xs font-bold flex items-center justify-center">{s.stepOrder}</span>
                    <span className="text-navy">{s.name}</span>
                    <span className="text-xs text-gray-400">
                      · {s.approverType.replace(/_/g, ' ')}
                      {s.approverType === 'specific_user'
                        ? (s.approverUserName ? ` → ${s.approverUserName}` : ' (unassigned)')
                        : (s.approverRole ? `: ${s.approverRole}` : '')}
                    </span>
                  </li>
                ))}
              </ol>
              {chain._count !== undefined && <p className="text-xs text-gray-400 mt-3">{chain._count.rules} routing rule(s)</p>}
            </Card>
          ))}
        </div>
      )}

      {building && <ChainBuilder onClose={() => setBuilding(false)} onSave={createChain} />}
      {editing && (
        <ChainBuilder chain={editing} onClose={() => setEditing(null)}
          onSave={data => updateChain(editing.id, data)} />
      )}
    </div>
  );
}
